import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'ff:chunk-reload';

type FlagStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Awaits a dynamic-import factory, recovering from the "stale chunk after deploy" failure.
 *
 * When a new build replaces the content-hashed chunks, a long-lived tab still running the
 * previous SPA may client-side-navigate and `import()` a chunk hash that no longer exists on
 * the server (HTTP 404/500 → "Importing a module script failed"). Instead of crashing the
 * route, we reload the page ONCE — guarded by a session flag — so the browser fetches the
 * fresh `index.html` (served `no-cache`) and its current chunk graph. A second failure after
 * the reload is a genuine error and is rethrown (no reload loop). On success the flag is
 * cleared so a future deploy can recover again.
 *
 * Exported for testing; `reload`/`storage` are injectable.
 */
export async function importWithReload<T>(
  factory: () => Promise<T>,
  reload: () => void = () => window.location.reload(),
  storage: FlagStorage = window.sessionStorage,
): Promise<T> {
  try {
    const mod = await factory();
    storage.removeItem(RELOAD_KEY);
    return mod;
  } catch (err) {
    if (storage.getItem(RELOAD_KEY)) {
      throw err; // already reloaded once this session → surface the real failure
    }
    storage.setItem(RELOAD_KEY, '1');
    reload();
    // The reload navigates away; resolve never so React keeps the Suspense fallback meanwhile.
    return new Promise<T>(() => {});
  }
}

/**
 * `React.lazy` with stale-chunk-after-deploy recovery (see {@link importWithReload}). Drop-in
 * replacement for `lazy(() => import('...'))` across the route table.
 */
export function lazyWithReload<T extends ComponentType>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => importWithReload(factory));
}
